import { formatDistanceToNow } from 'date-fns'
import { Copy, MoreHorizontal, Pencil, Play, Presentation as PresentationIcon, Trash2, Type, Users } from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card } from 'components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'

import type { PresentationSummary } from '../types/presentation.types'

interface PresentationCardProps {
	presentation: PresentationSummary
	onPresent: () => void
	onEdit: () => void
	onDuplicate: () => void
	onRename: () => void
	onDelete: () => void
	/** The owner's "Share with partners..." (MYD-526). */
	onShare?: () => void
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/** "3 days ago", or nothing for a timestamp that does not parse. */
const updatedLabel = (iso: string) => {
	const date = new Date(iso)
	return Number.isNaN(date.getTime()) ? '' : `Updated ${formatDistanceToNow(date, { addSuffix: true })}`
}

/**
 * One deck in the gallery (MYD-494).
 *
 * What a card offers depends on `isOwner`, which the server computes. A
 * partner's shared deck gets Present and Duplicate only: sharing hands over
 * the layout, not control of it, and the server would 404 any edit anyway.
 * Offering buttons that fail is worse than not offering them.
 *
 * A deck with no slides cannot be presented, so Present is disabled rather
 * than opening an empty presenter.
 */
/**
 * The badge on a card: for a partner, that it was shared with them; for the
 * owner, who it is shared with (MYD-526). Nothing on a deck only its owner sees.
 */
export const sharingBadge = ({ visibility, isOwner, sharedWithCount }: Pick<PresentationSummary, 'visibility' | 'isOwner' | 'sharedWithCount'>): string | null => {
	if (!isOwner) return 'Shared with you'
	if (visibility === 'shared') return 'Shared with all partners'
	if (visibility === 'selected' && (sharedWithCount ?? 0) > 0) return `Shared with ${sharedWithCount} ${sharedWithCount === 1 ? 'partner' : 'partners'}`
	return null
}

export function PresentationCard({ presentation, onPresent, onEdit, onDuplicate, onRename, onDelete, onShare }: PresentationCardProps) {
	const { name, coverImage, storyCount, slideCount, updatedAt, isOwner } = presentation
	const canPresent = slideCount > 0
	const badge = sharingBadge(presentation)

	return (
		<Card className="group flex flex-col overflow-hidden hover:shadow-md transition-shadow">
			<button
				type="button"
				// The card itself opens the most useful thing for this viewer.
				onClick={isOwner ? onEdit : onPresent}
				disabled={!isOwner && !canPresent}
				className="relative block aspect-video w-full bg-muted text-left disabled:cursor-default"
				aria-label={isOwner ? `Edit ${name}` : `Present ${name}`}
			>
				{coverImage ? (
					<img src={coverImage} alt="" className="h-full w-full object-cover" loading="lazy" />
				) : (
					<span className="flex h-full w-full items-center justify-center">
						<PresentationIcon className="w-10 h-10 text-gray-300" />
					</span>
				)}

				{badge && (
					<Badge variant={isOwner ? 'default' : 'secondary'} className="absolute left-2 top-2 gap-1">
						<Users className="w-3 h-3" />
						{badge}
					</Badge>
				)}
			</button>

			<div className="flex flex-1 flex-col gap-1 p-4">
				<h3 className="truncate text-base font-semibold text-gray-900" title={name}>
					{name}
				</h3>
				<p className="text-xs text-gray-500">
					{plural(storyCount, 'story', 'stories')} · {plural(slideCount, 'slide', 'slides')}
				</p>
				<p className="text-xs text-gray-400">{updatedLabel(updatedAt)}</p>

				<div className="mt-auto flex items-center gap-2 pt-3">
					<Button onClick={onPresent} disabled={!canPresent} title={canPresent ? undefined : 'Add a slide to present this deck'}>
						<Play className="w-3.5 h-3.5 mr-1" />
						Present
					</Button>

					{isOwner && (
						<Button variant="ghost" isOutline onClick={onEdit}>
							<Pencil className="w-3.5 h-3.5 mr-1" />
							Edit
						</Button>
					)}

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className={cn(
									'ml-auto rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900',
									'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
								)}
								aria-label={`More actions for ${name}`}
							>
								<MoreHorizontal className="w-4 h-4" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={onDuplicate}>
								<Copy className="w-4 h-4 mr-2" />
								Duplicate
							</DropdownMenuItem>
							{isOwner && (
								<>
									<DropdownMenuItem onClick={onRename}>
										<Type className="w-4 h-4 mr-2" />
										Rename
									</DropdownMenuItem>
									{onShare && (
										<DropdownMenuItem onClick={onShare}>
											<Users className="w-4 h-4 mr-2" />
											Share with partners...
										</DropdownMenuItem>
									)}
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
										<Trash2 className="w-4 h-4 mr-2" />
										Delete
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
		</Card>
	)
}

export default PresentationCard
